import { useEffect, useMemo, useState } from 'react';
import { Form, Button, Col, Row, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import {
  FaPlus,
  FaRegTrashAlt,
  FaEye,
  FaEraser,
  FaFileInvoiceDollar,
  FaUserTie,
  FaBoxes,
  FaCreditCard,
  FaLink,
  FaCalculator,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaBuilding,
} from 'react-icons/fa';
import api from '../../api/apiweb-factura.js';
import apiReceptores from '../../api/apiweb-receptoresdteweb.js';
import Rut from 'rutjs';
import { TIPOS_REFERENCIA_NOTA } from '../../constants/tiposReferencia';
import regionesData from '../../constants/regiones_comunas.json';
import { v4 as uuidv4 } from 'uuid';
import { calcularLinea, sumarLineas } from '../../utils/montosDte';
import { erroresDeRespuesta, mensajeDeRespuesta } from '../../utils/erroresApi';
import { consultarEstadoDte } from '../../utils/estadoDte';
import useSucursales from '../../hooks/useSucursales';
import SelectorSucursal from '../../components/SelectorSucursal';
import '../factura/Factura.scss';

// Códigos de referencia (CodRef) exigidos por el SII para notas de crédito/débito.
const CODIGOS_REFERENCIA = [
  { value: '1', label: '1 - Anula documento de referencia' },
  { value: '2', label: '2 - Corrige texto documento de referencia' },
  { value: '3', label: '3 - Corrige montos' },
];

function NotaCreditoDebitoFormulario(props) {
  const tipoDocumento = props.tipoDocumento || '61'; // 61 = Nota de Crédito, 56 = Nota de Débito
  const esNotaCredito = tipoDocumento === '61';
  const nombreDoc = esNotaCredito ? 'Nota de Crédito Electrónica' : 'Nota de Débito Electrónica';

  // La nota de débito NO admite CodRef = 2 (corrige texto); el SII la rechaza.
  // Solo la nota de crédito puede corregir texto.
  // Además, una nota de débito sobre una nota de crédito solo se usa para anularla:
  // corregirle montos dejaría dos documentos vivos y un rastro contable confuso.
  const codigosReferenciaPara = (tipoDocRef) => {
    if (esNotaCredito) return CODIGOS_REFERENCIA;
    if (tipoDocRef === '61') return CODIGOS_REFERENCIA.filter((cod) => cod.value === '1');
    return CODIGOS_REFERENCIA.filter((cod) => cod.value !== '2');
  };

  const initialState = {
    emisor: {
      rut: '', razonSocial: '', direccion: '', comuna: '', ciudad: '',
      tipoVenta: '1', acteco: '', email: '', telefono: '', giro: '',
    },
    receptor: {
      rut: '', razonSocial: '', direccion: '',
      comuna: '', ciudad: '', giro: '', contacto: '', rutSolicita: '', email: '',
    },
    productos: [],
    formaPago: '1',
    // La referencia es OBLIGATORIA en una nota de crédito/débito: siempre parte con una fila.
    referencias: [{ tipoDoc: '', folio: '', fecha: '', codRef: '', razon: '' }],
  };

  const [formData, setFormData] = useState(initialState);
  const [esExenta, setEsExenta] = useState(false);
  const [rutReceptorError, setRutReceptorError] = useState('');
  const [rutSolicitaError, setRutSolicitaError] = useState('');
  const [emailReceptorError, setEmailReceptorError] = useState('');
  const [erroresReceptor, setErroresReceptor] = useState([]);
  const [mostrarModalErrores, setMostrarModalErrores] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorApi, setErrorApi] = useState(null);
  const [mostrarModalErrorApi, setMostrarModalErrorApi] = useState(false);
  const [erroresProducto, setErroresProducto] = useState([]);
  const [mostrarModalErroresProducto, setMostrarModalErroresProducto] = useState(false);
  const [mostrarModalReferenciaInvalida, setMostrarModalReferenciaInvalida] = useState(false);
  const [erroresReferencia, setErroresReferencia] = useState([]);
  const [mostrarModalPdf, setMostrarModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [jsonPendienteEnvio, setJsonPendienteEnvio] = useState(null);
  const [mostrarModalEnvio, setMostrarModalEnvio] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [buscandoReceptor, setBuscandoReceptor] = useState(false);
  const [estadoEnvio, setEstadoEnvio] = useState({ estado: 'PROCESANDO', data: null });
  const [inputsTemporales, setInputsTemporales] = useState({});
  const [actecoOpciones, setActecoOpciones] = useState([]);
  const [fechaEmision, setFechaEmision] = useState('');
  const [urlLogo, setUrlLogo] = useState('');

  // Sucursal desde la que se emite: su dirección es la que queda impresa en el DTE.
  const {
    sucursales,
    sucursal,
    sucursalId,
    setSucursalId,
    cargando: cargandoSucursales,
    recargar: recargarSucursales,
    faltaSeleccion: faltaSucursal,
  } = useSucursales(props.empresas?.[0]?.rut);

  // Con sucursal elegida manda la suya; sin sucursales cargadas, la de la empresa.
  const direccionEmisor = sucursal ? sucursal.direccion : formData.emisor.direccion;
  const comunaEmisor = sucursal ? sucursal.comuna : formData.emisor.comuna;
  const ciudadEmisor = sucursal ? sucursal.ciudad : formData.emisor.ciudad;

  const subtotal = sumarLineas(formData.productos);

  const montoNeto = subtotal;
  const tasaIVA = 19;
  const montoIVA = esExenta ? 0 : Math.round(montoNeto * (tasaIVA / 100));
  const total = Math.round(montoNeto + montoIVA);

  const [visibleSections, setVisibleSections] = useState({
    emisor: true,
    receptor: true,
  });

  const groupedRegionOptions = useMemo(
    () =>
      regionesData.regiones.map((r) => ({
        label: r.region,
        options: r.comunas.map((c) => ({ value: c, label: c })),
      })),
    [],
  );

  const findComunaOption = (value) => {
    if (!value) return null;
    for (const group of groupedRegionOptions) {
      const found = group.options.find(
        (op) => op.value.toUpperCase() === value.toUpperCase(),
      );
      if (found) return found;
    }
    return null;
  };

  const handleComunaReceptorChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      receptor: { ...prev.receptor, comuna: selected ? selected.value : '' },
    }));
  };

  const eliminarReferencia = (index) => {
    const nuevasReferencias = [...formData.referencias];
    nuevasReferencias.splice(index, 1);
    setFormData((prev) => ({ ...prev, referencias: nuevasReferencias }));
  };

  const toggleSection = (section) => {
    setVisibleSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (e, section, field) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleInputChangeUpper = (e, section, field) => {
    const value = e.target.value.toUpperCase();
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const actualizarProducto = (index, campo, valor) => {
    setFormData((prev) => {
      const nuevosProductos = [...prev.productos];
      nuevosProductos[index] = { ...nuevosProductos[index], [campo]: valor };
      return { ...prev, productos: nuevosProductos };
    });
  };

  const handleFormaPagoChange = (e) => {
    setFormData((prev) => ({ ...prev, formaPago: e.target.value }));
  };

  const handleLimpiarFormulario = () => {
    setFormData((prev) => ({
      ...initialState,
      emisor: { ...prev.emisor },
    }));
    setRutReceptorError('');
    setRutSolicitaError('');
    setEmailReceptorError('');
    setErroresReceptor([]);
    setMostrarModalErrores(false);
    setInputsTemporales({});
  };

  const eliminarProducto = (index) => {
    const nuevosProductos = [...formData.productos];
    nuevosProductos.splice(index, 1);
    setFormData((prev) => ({ ...prev, productos: nuevosProductos }));
  };

  const agregarReferencia = () => {
    setFormData((prev) => ({
      ...prev,
      referencias: [...prev.referencias, { tipoDoc: '', folio: '', fecha: '', codRef: '', razon: '' }],
    }));
  };

  const actualizarReferencia = (index, campo, valor) => {
    const nuevasReferencias = [...formData.referencias];
    nuevasReferencias[index] = { ...nuevasReferencias[index], [campo]: valor };
    if (campo === 'tipoDoc') {
      // El tipo elegido puede restringir los CodRef disponibles: limpiamos el que quedó fuera.
      const permitidos = codigosReferenciaPara(valor).map((cod) => cod.value);
      if (!permitidos.includes(nuevasReferencias[index].codRef)) {
        nuevasReferencias[index].codRef = '';
      }
    }
    setFormData((prev) => ({ ...prev, referencias: nuevasReferencias }));
  };

  const generarJsonNota = () => {
    const lineas = formData.productos.map((producto) => calcularLinea(producto));
    const subtotalDetalle = lineas.reduce((acc, linea) => acc + linea.monto, 0);

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaemisionDate = new Date(fechaEmision);
    const errores = [];
    if (fechaEmision === '') {
      errores.push('La fecha de emisión no puede ser nula.');
      setMostrarModalErrores(true);
      setErroresReceptor(errores);
      return false;
    }
    if (fechaemisionDate > hoy) {
      errores.push('La fecha de emisión no puede ser mayor al día actual.');
      setMostrarModalErrores(true);
      setErroresReceptor(errores);
      return false;
    }
    if (faltaSucursal) {
      errores.push('Debes seleccionar la sucursal desde la que se emite el documento.');
      setMostrarModalErrores(true);
      setErroresReceptor(errores);
      return false;
    }
    if (!validarReceptor()) return false;
    if (!validarProductos()) return false;
    if (!validarReferencias()) return false;

    const iva = esExenta ? 0 : Math.round((subtotalDetalle * tasaIVA) / 100);
    const totalDoc = Math.round(subtotalDetalle + iva);

    const jsonGenerado = {
      ID: uuidv4(),
      Encabezado: {
        IdDoc: { TipoDTE: tipoDocumento, FchEmis: fechaEmision, TpoTranVenta: formData.emisor.tipoVenta, FmaPago: formData.formaPago },
        Emisor: {
          RUTEmisor: new Rut(formData.emisor.rut).rut,
          RznSoc: formData.emisor.razonSocial,
          GiroEmis: formData.emisor.giro,
          CorreoEmisor: formData.emisor.email,
          Acteco: formData.emisor.acteco,
          // El backend resuelve dirección, comuna, ciudad y CdgSIISucur a partir de
          // la sucursal; mandamos igual los suyos para que la previsualización
          // muestre lo mismo que el documento final.
          ...(sucursal && { SucursalId: Number(sucursal.sucursal_id) }),
          DirOrigen: direccionEmisor,
          CmnaOrigen: comunaEmisor,
          CiudadOrigen: ciudadEmisor,
          TipoVenta: formData.emisor.tipoVenta,
          Telefono: formData.emisor.telefono,
        },
        Receptor: {
          RUTRecep: new Rut(formData.receptor.rut).rut,
          RznSocRecep: formData.receptor.razonSocial,
          GiroRecep: formData.receptor.giro,
          DirRecep: formData.receptor.direccion,
          CmnaRecep: formData.receptor.comuna.toUpperCase(),
          CiudadRecep: formData.receptor.ciudad,
          Contacto: formData.receptor.contacto,
          ...(formData.receptor.email.trim() && { CorreoRecep: formData.receptor.email.trim() }),
          RutSolicita: formData.receptor.rutSolicita,
        },
        Totales: esExenta
          ? { MntExe: totalDoc, MntTotal: totalDoc }
          : { MntNeto: subtotalDetalle, TasaIVA: tasaIVA, IVA: iva, MntTotal: totalDoc },
      },
      Detalle: lineas.map((linea, index) => {
        const producto = formData.productos[index];
        const detalleItem = {
          NroLinDet: index + 1,
          NmbItem: producto.nombre,
          DscItem: producto.descripcion,
          QtyItem: linea.cantidad,
          PrcItem: linea.precio,
          UnmdItem: producto.unidad,
          MontoItem: linea.monto,
        };
        if (linea.descuento > 0) {
          detalleItem.DescuentoPct = +linea.descuento.toFixed(2);
          detalleItem.DescuentoMonto = linea.montoDescuento;
        }
        return detalleItem;
      }),
      // La referencia es obligatoria: siempre se incluye.
      Referencia: formData.referencias.map((referencia, index) => ({
        NroLinRef: index + 1,
        TpoDocRef: referencia.tipoDoc,
        FolioRef: referencia.folio,
        FchRef: referencia.fecha,
        CodRef: referencia.codRef,
        RazonRef: referencia.razon,
      })),
    };
    return jsonGenerado;
  };

  const base64PdfToUrl = (base64) => {
    // El backend puede devolver el PDF doble-codificado en base64:
    // una primera decodificación entrega otra cadena base64 (que empieza con "JVBER" = "%PDF").
    let datos = atob(base64);
    if (datos.startsWith('JVBER')) datos = atob(datos);
    const byteNumbers = new Array(datos.length).fill().map((_, i) => datos.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  };

  const handleVisuavilizar = async () => {
    setLoading(true);
    try {
      const dte = generarJsonNota();
      if (!dte) return;
      const json = { dte, preview: true };
      const response = await api.previewFactura(json);
      const url = base64PdfToUrl(response.data);
      setPdfUrl(url);
      setJsonPendienteEnvio(json.dte);
      setMostrarModalPdf(true);
    } catch (error) {
      console.error('Error al previsualizar la nota:', error.message);
      setErrorApi('Ocurrió un error al generar el PDF.');
      setMostrarModalErrorApi(true);
    } finally {
      setLoading(false);
    }
  };

  const validarReceptor = () => {
    const receptor = formData.receptor;
    const errores = [];
    const rutValido = new Rut(receptor.rut).validate();
    if (!rutValido) {
      errores.push('El RUT del receptor es inválido.');
      setRutReceptorError('RUT inválido');
    } else {
      setRutReceptorError('');
    }

    const camposObligatorios = [
      { campo: 'razonSocial', etiqueta: 'Razón Social' },
      { campo: 'direccion', etiqueta: 'Dirección' },
      { campo: 'comuna', etiqueta: 'Comuna' },
      { campo: 'ciudad', etiqueta: 'Ciudad' },
      { campo: 'giro', etiqueta: 'Giro' },
    ];

    if (receptor.razonSocial.length > 100) errores.push('Razón Social no puede superar los 100 caracteres.');
    if (receptor.giro.length > 80) errores.push('Giro no puede superar los 80 caracteres.');
    if (receptor.contacto.length > 80) errores.push('Contacto no puede superar los 80 caracteres.');
    if (receptor.direccion.length > 70) errores.push('Dirección no puede superar los 70 caracteres.');
    if (receptor.comuna.length > 20) errores.push('Comuna no puede superar los 20 caracteres.');
    if (receptor.ciudad.length > 20) errores.push('Ciudad no puede superar los 20 caracteres.');

    camposObligatorios.forEach(({ campo, etiqueta }) => {
      if (!receptor[campo] || receptor[campo].trim() === '') {
        errores.push(`El campo "${etiqueta}" es obligatorio.`);
      }
    });

    if (receptor.email && receptor.email.trim() !== '') {
      if (!EMAIL_REGEX.test(receptor.email.trim())) {
        errores.push('El email del receptor es inválido.');
        setEmailReceptorError('Email inválido');
      } else if (receptor.email.length > 80) {
        errores.push('El email del receptor no puede superar los 80 caracteres.');
      } else {
        setEmailReceptorError('');
      }
    }

    if (receptor.rutSolicita && receptor.rutSolicita.trim() !== '') {
      const rutSolicitaValido = new Rut(receptor.rutSolicita).validate();
      if (!rutSolicitaValido) {
        errores.push('El RUT de quien solicita es inválido.');
        setRutSolicitaError('RUT inválido');
      } else {
        setRutSolicitaError('');
      }
    }

    if (errores.length > 0) {
      setErroresReceptor(errores);
      setMostrarModalErrores(true);
      return false;
    }
    return true;
  };

  const validarProductos = () => {
    const errores = [];
    if (formData.productos.length === 0) {
      errores.push('Debe agregar al menos un producto.');
    }
    formData.productos.forEach((producto, index) => {
      const numeroFila = index + 1;
      if (!producto.nombre || producto.nombre.trim() === '') errores.push(`Fila ${numeroFila}: El nombre del producto es obligatorio.`);
      else if (producto.nombre.length > 80) errores.push(`Fila ${numeroFila}: El nombre no puede superar los 80 caracteres.`);
      if (producto.descripcion && producto.descripcion.length > 1000) errores.push(`Fila ${numeroFila}: La descripción adicional no puede superar los 1000 caracteres.`);
      if (producto.unidad && producto.unidad.length > 4) errores.push(`Fila ${numeroFila}: La unidad no puede superar los 4 caracteres.`);
      if (producto.cantidad === undefined || isNaN(producto.cantidad) || parseFloat(producto.cantidad) <= 0) errores.push(`Fila ${numeroFila}: La cantidad debe ser un número mayor a 0.`);
      if (producto.precio === undefined || isNaN(producto.precio) || parseFloat(producto.precio) <= 0) errores.push(`Fila ${numeroFila}: El precio debe ser un número mayor a 0.`);
      if (producto.descuento !== undefined && (!/^\d{0,3}(\.\d{1,2})?$/.test(producto.descuento) || parseFloat(producto.descuento) > 100)) {
        errores.push(`Fila ${numeroFila}: El descuento debe ser un número entre 0 y 100 con hasta 2 decimales.`);
      }
    });
    if (errores.length > 0) {
      setErroresProducto(errores);
      setMostrarModalErroresProducto(true);
      return false;
    }
    return true;
  };

  const validarReferencias = () => {
    const errores = [];
    // La referencia es obligatoria para notas de crédito/débito.
    if (formData.referencias.length === 0) {
      errores.push('Debe agregar al menos una referencia al documento que modifica esta nota.');
    }
    formData.referencias.forEach((ref, index) => {
      const fila = index + 1;
      if (!ref.tipoDoc || ref.tipoDoc.trim() === '') errores.push(`Referencia ${fila}: El tipo de documento es obligatorio.`);
      if (!ref.folio || ref.folio.trim() === '') errores.push(`Referencia ${fila}: El folio es obligatorio.`);
      if (!ref.codRef || ref.codRef.trim() === '') {
        errores.push(`Referencia ${fila}: El código de referencia es obligatorio.`);
      } else if (!codigosReferenciaPara(ref.tipoDoc).some((cod) => cod.value === ref.codRef)) {
        errores.push(
          `Referencia ${fila}: Una ${esNotaCredito ? 'nota de crédito' : 'nota de débito'} no admite ese código de referencia para el documento seleccionado.`,
        );
      }
      if (!ref.fecha || ref.fecha.trim() === '') {
        errores.push(`Referencia ${fila}: La fecha es obligatoria.`);
      } else {
        const fechaReferencia = new Date(ref.fecha);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        if (fechaReferencia > hoy) errores.push(`Referencia ${fila}: La fecha no puede ser mayor al día actual.`);
      }
      if (ref.razon && ref.razon.length > 90) errores.push(`Referencia ${fila}: La razón no puede exceder los 90 caracteres.`);
    });
    if (errores.length > 0) {
      setErroresReferencia(errores);
      setMostrarModalReferenciaInvalida(true);
      return false;
    }
    return true;
  };

  const formatearMiles = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '';
    const [enteros, decimales] = valor.toFixed(2).split('.');
    const miles = enteros.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimales !== '00' ? `${miles},${decimales}` : miles;
  };

  const autocompletarReceptor = async (rutObj) => {
    setBuscandoReceptor(true);
    try {
      const { data } = await apiReceptores.getReceptores({ rutExacto: rutObj.rut, limit: 1 });
      const receptor = data?.body?.[0];
      setFormData((prev) => ({
        ...prev,
        receptor: {
          ...prev.receptor,
          razonSocial: (receptor?.razon_social || '').toUpperCase(),
          giro: (receptor?.giro || '').toUpperCase(),
          direccion: (receptor?.direccion || '').toUpperCase(),
          comuna: (receptor?.comuna || '').toUpperCase(),
          ciudad: (receptor?.ciudad || '').toUpperCase(),
          email: receptor?.email || '',
        },
      }));
      setEmailReceptorError('');
    } catch (error) {
      console.error('Error al autocompletar receptor:', error);
    } finally {
      setBuscandoReceptor(false);
    }
  };

  const handleRutReceptorChange = (e) => {
    const input = e.target.value;
    const limpio = input.replace(/[^0-9kK]/g, '');
    if (limpio === '') {
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rut: '' } }));
      setRutReceptorError('');
      return;
    }
    try {
      const rutObj = new Rut(limpio);
      const rutFormateado = rutObj.getNiceRut();
      const esValido = rutObj.validate();
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rut: rutFormateado } }));
      setRutReceptorError(esValido ? '' : 'RUT inválido');
      if (esValido) autocompletarReceptor(rutObj);
    } catch (err) {
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rut: input } }));
      setRutReceptorError('RUT inválido');
    }
  };

  const handleRutSolicitaChange = (e) => {
    const input = e.target.value;
    const limpio = input.replace(/[^0-9kK]/g, '');
    if (limpio === '') {
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rutSolicita: '' } }));
      setRutSolicitaError('');
      return;
    }
    try {
      const rutObj = new Rut(limpio);
      const rutFormateado = rutObj.getNiceRut();
      const esValido = rutObj.validate();
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rutSolicita: rutFormateado } }));
      setRutSolicitaError(esValido ? '' : 'RUT inválido');
    } catch (err) {
      setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, rutSolicita: input } }));
      setRutSolicitaError('RUT inválido');
    }
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleEmailReceptorChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, receptor: { ...prev.receptor, email: value } }));
    if (value.trim() === '' || EMAIL_REGEX.test(value.trim())) {
      setEmailReceptorError('');
    } else {
      setEmailReceptorError('Email inválido');
    }
  };

  useEffect(() => {
    const cargarDatosEmpresa = async () => {
      if (props.empresas && props.empresas.length > 0) {
        const empresa = props.empresas[0];
        const nicerut = new Rut('' + empresa.rut, true).getNiceRut();
        if (empresa.logo != null) setUrlLogo(empresa.logo);
        try {
          const actecoData = (await api.getActeco()).data.records;
          const artecoOpc = actecoData.map((item) => ({ value: item.codigo, label: `${item.nombre}` }));
          setActecoOpciones(artecoOpc);
          setFormData((prev) => ({
            ...prev,
            emisor: {
              rut: nicerut || '',
              razonSocial: empresa.name || '',
              direccion: empresa.direccion || '',
              comuna: empresa.comuna || '',
              ciudad: empresa.ciudad || '',
              email: empresa.email || '',
              telefono: empresa.telefono || '',
              giro: empresa.giro || '',
              tipoVenta: '1',
              acteco: actecoData.length > 0 ? actecoData[0].codigo : '',
            },
          }));
        } catch (error) {
          console.error('Error al obtener Acteco:', error);
        }
      }
    };
    cargarDatosEmpresa();
  }, [props.empresas]);

  const handleEnviarNota = async () => {
    const idDoc = jsonPendienteEnvio?.ID;
    if (!idDoc) return;

    setMostrarModalPdf(false);
    setEstadoEnvio({ estado: 'PROCESANDO', data: null });
    setMostrarModalEnvio(true);

    try {
      await api.createFactura(jsonPendienteEnvio);
      setEstadoEnvio(await consultarEstadoDte(idDoc));
    } catch (error) {
      console.error('Error al enviar la nota:', error);
      const errores = erroresDeRespuesta(error);
      const mensaje = mensajeDeRespuesta(error);
      // Si la sucursal elegida ya no existe o la dieron de baja mientras la vista
      // estaba abierta, hay que volver a pedir la lista para que elija de nuevo.
      if (mensaje && mensaje.toLowerCase().includes('sucursal')) recargarSucursales();
      setEstadoEnvio(
        errores
          ? { estado: 'RECHAZADO', data: null, errores }
          : { estado: 'ERROR', data: null, mensaje },
      );
    }
  };

  const descargarPdfGenerado = async () => {
    const { folio, fechaEmision: fchEmis } = estadoEnvio.data || {};
    if (!folio) return;
    setDescargandoPdf(true);
    try {
      const response = await api.pdfFacturaGenerada({
        rut: new Rut(formData.emisor.rut).rut,
        tipodocumento: tipoDocumento,
        folio,
        fechaEmision: fchEmis,
      });
      if (response.data) window.open(response.data, '_blank');
    } catch (error) {
      console.error('Error al obtener el PDF de la nota:', error);
      setErrorApi('Ocurrió un error al obtener el PDF de la nota.');
      setMostrarModalErrorApi(true);
    } finally {
      setDescargandoPdf(false);
    }
  };

  const cerrarModalEnvio = () => {
    // Si la nota se generó, limpiamos el formulario para una nueva emisión.
    if (estadoEnvio.estado === 'GENERADO') {
      handleLimpiarFormulario();
      setJsonPendienteEnvio(null);
      setPdfUrl(null);
    }
    setMostrarModalEnvio(false);
    setEstadoEnvio({ estado: 'PROCESANDO', data: null });
  };

  const SectionHeader = ({ section, icon, title }) => (
    <div className="section-header" onClick={() => toggleSection(section)} role="button">
      <div className="section-header-title">
        {icon}
        <span>{title}</span>
      </div>
      {visibleSections[section] ? <FaChevronUp /> : <FaChevronDown />}
    </div>
  );

  return (
    <div className="Factura">
      <div className="page-header">
        <h1 className="page-title">{nombreDoc}</h1>
        <p className="page-subtitle">
          {esNotaCredito
            ? 'Genera y previsualiza notas de crédito electrónicas (anula, corrige texto o corrige montos)'
            : 'Genera y previsualiza notas de débito electrónicas'}
        </p>
      </div>

      <div className="factura-summary-bar">
        <div className="factura-summary-logo">
          {urlLogo && <img src={urlLogo} alt="Logo" />}
        </div>
        <div className="factura-summary-info">
          <div className="factura-doc-type">{nombreDoc}</div>
          <div className="factura-doc-rut">RUT {formData.emisor.rut || '—'}</div>
          <div className="factura-doc-folio">N° folio no asignado</div>
          <Row className="g-2">
            <Col xs={6}>
              <Form.Group className="factura-doc-fecha">
                <Form.Label>Tipo de monto</Form.Label>
                <Form.Select
                  value={esExenta ? 'exenta' : 'afecta'}
                  onChange={(e) => setEsExenta(e.target.value === 'exenta')}
                >
                  <option value="afecta">Afecta (con IVA)</option>
                  <option value="exenta">Exenta</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="factura-doc-fecha">
                <Form.Label>Fecha emisión</Form.Label>
                <Form.Control
                  type="date"
                  value={fechaEmision}
                  onChange={(e) => setFechaEmision(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      <div className="form-section">
        <SectionHeader section="emisor" icon={<FaBuilding />} title="Datos del Emisor" />
        {visibleSections.emisor && (
          <div className="form-section-body">
            <Row className="g-3">
              <Col md={8}>
                <Form.Label>Razón Social</Form.Label>
                <Form.Control type="text" value={formData.emisor.razonSocial} readOnly />
              </Col>
              <SelectorSucursal
                sucursales={sucursales}
                sucursalId={sucursalId}
                onChange={setSucursalId}
                cargando={cargandoSucursales}
              />
              <Col md={6}>
                <Form.Label>Dirección</Form.Label>
                <Form.Control type="text" value={direccionEmisor} readOnly />
              </Col>
              <Col md={3}>
                <Form.Label>Comuna</Form.Label>
                <Form.Control type="text" value={comunaEmisor} readOnly />
              </Col>
              <Col md={3}>
                <Form.Label>Ciudad</Form.Label>
                <Form.Control type="text" value={ciudadEmisor} readOnly />
              </Col>
              <Col md={3}>
                <Form.Label>Teléfono</Form.Label>
                <Form.Control type="text" value={formData.emisor.telefono} readOnly />
              </Col>
              <Col md={5}>
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={formData.emisor.email} readOnly />
              </Col>
              <Col md={8}>
                <Form.Label>Giro</Form.Label>
                <Form.Control type="text" value={formData.emisor.giro} readOnly />
              </Col>
              <Col md={6}>
                <Form.Label>Tipo de Venta</Form.Label>
                <Form.Select
                  value={formData.emisor.tipoVenta}
                  onChange={(e) => handleInputChange(e, 'emisor', 'tipoVenta')}
                >
                  <option value="1">Ventas del Giro</option>
                  <option value="2">Venta Activo Fijo</option>
                  <option value="3">Venta Bien Raíz</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Actividad económica</Form.Label>
                <Form.Select
                  value={formData.emisor.acteco || ''}
                  onChange={(e) => handleInputChange(e, 'emisor', 'acteco')}
                >
                  {actecoOpciones.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </div>
        )}
      </div>

      <div className="form-section">
        <SectionHeader section="receptor" icon={<FaUserTie />} title="Datos del Receptor" />
        {visibleSections.receptor && (
          <div className="form-section-body">
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>RUT</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="12.345.678-9"
                  value={formData.receptor.rut}
                  onChange={handleRutReceptorChange}
                  isInvalid={!!rutReceptorError}
                />
                <Form.Control.Feedback type="invalid">{rutReceptorError}</Form.Control.Feedback>
                {buscandoReceptor && (
                  <Form.Text className="text-muted d-flex align-items-center mt-1">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Buscando datos del receptor…
                  </Form.Text>
                )}
              </Col>
              <Col md={8}>
                <Form.Label>Razón Social</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Razón Social"
                  value={formData.receptor.razonSocial}
                  onChange={(e) => handleInputChangeUpper(e, 'receptor', 'razonSocial')}
                  maxLength={100}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Dirección"
                  value={formData.receptor.direccion}
                  onChange={(e) => handleInputChangeUpper(e, 'receptor', 'direccion')}
                  maxLength={70}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Comuna</Form.Label>
                <Select
                  options={groupedRegionOptions}
                  value={findComunaOption(formData.receptor.comuna)}
                  onChange={handleComunaReceptorChange}
                  isClearable
                  placeholder="Selecciona comuna"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Ciudad</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ciudad"
                  value={formData.receptor.ciudad}
                  onChange={(e) => handleInputChangeUpper(e, 'receptor', 'ciudad')}
                  maxLength={20}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Contacto</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Contacto"
                  value={formData.receptor.contacto}
                  onChange={(e) => handleInputChangeUpper(e, 'receptor', 'contacto')}
                  maxLength={80}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="correo@ejemplo.cl"
                  value={formData.receptor.email}
                  onChange={handleEmailReceptorChange}
                  isInvalid={!!emailReceptorError}
                  maxLength={80}
                />
                <Form.Control.Feedback type="invalid">{emailReceptorError}</Form.Control.Feedback>
              </Col>
              <Col md={4}>
                <Form.Label>RUT Solicita</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="12.345.678-9"
                  value={formData.receptor.rutSolicita}
                  onChange={handleRutSolicitaChange}
                  isInvalid={!!rutSolicitaError}
                />
                <Form.Control.Feedback type="invalid">{rutSolicitaError}</Form.Control.Feedback>
              </Col>
              <Col md={8}>
                <Form.Label>Giro</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Giro"
                  value={formData.receptor.giro}
                  onChange={(e) => handleInputChangeUpper(e, 'receptor', 'giro')}
                  maxLength={80}
                />
              </Col>
            </Row>
          </div>
        )}
      </div>

      {/* Referencia OBLIGATORIA: documento que la nota modifica */}
      <div className="form-section">
        <div className="section-header section-header-static">
          <div className="section-header-title">
            <FaLink />
            <span>Documento de referencia</span>
            <span className="section-badge">Obligatorio</span>
          </div>
        </div>
        <div className="form-section-body p-0">
          <div className="table-responsive">
            <table className="data-table data-table-form">
              <thead>
                <tr>
                  <th style={{ minWidth: 180 }}>Tipo de Documento</th>
                  <th style={{ width: 130 }}>Folio Ref.</th>
                  <th style={{ width: 160 }}>Fecha Ref.</th>
                  <th style={{ minWidth: 220 }}>Código Referencia</th>
                  <th>Razón Referencia</th>
                  <th className="text-center" style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.referencias.map((referencia, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Select
                        value={referencia.tipoDoc || ''}
                        onChange={(e) => actualizarReferencia(index, 'tipoDoc', e.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {TIPOS_REFERENCIA_NOTA.map((tipo) => (
                          <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control type="text" placeholder="Folio" maxLength={18}
                        value={referencia.folio || ''}
                        onChange={(e) => actualizarReferencia(index, 'folio', e.target.value)} />
                    </td>
                    <td>
                      <Form.Control type="date"
                        value={referencia.fecha || ''}
                        onChange={(e) => actualizarReferencia(index, 'fecha', e.target.value)} />
                    </td>
                    <td>
                      <Form.Select
                        value={referencia.codRef || ''}
                        onChange={(e) => actualizarReferencia(index, 'codRef', e.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {codigosReferenciaPara(referencia.tipoDoc).map((cod) => (
                          <option key={cod.value} value={cod.value}>{cod.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control type="text" placeholder="Razón" maxLength={90}
                        value={referencia.razon || ''}
                        onChange={(e) => actualizarReferencia(index, 'razon', e.target.value)} />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Eliminar referencia"
                        onClick={() => eliminarReferencia(index)}
                        className="row-action-btn"
                        disabled={formData.referencias.length === 1}
                      >
                        <FaRegTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-section-action">
            <Button variant="primary" onClick={agregarReferencia}>
              <FaPlus className="me-2" />Agregar referencia
            </Button>
          </div>
        </div>
      </div>

      <div className="form-section">
        <div className="section-header section-header-static">
          <div className="section-header-title">
            <FaBoxes />
            <span>Detalle</span>
            {formData.productos.length > 0 && (
              <span className="section-badge">{formData.productos.length}</span>
            )}
          </div>
        </div>
        <div className="form-section-body p-0">
          {formData.productos.length === 0 ? (
            <div className="empty-state">
              <FaBoxes className="empty-state-icon" />
              <p className="empty-state-text">No hay líneas de detalle. Agrega al menos una para emitir la nota.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table data-table-form">
                <thead>
                  <tr>
                    <th style={{ minWidth: 150 }}>Nombre</th>
                    <th style={{ minWidth: 180 }}>Descripción</th>
                    <th style={{ width: 110 }}>Cantidad</th>
                    <th style={{ width: 80 }}>Unidad</th>
                    <th style={{ width: 130 }}>Precio</th>
                    <th style={{ width: 100 }}>Desc. (%)</th>
                    <th className="text-end" style={{ width: 130 }}>Subtotal</th>
                    <th className="text-center" style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.productos.map((producto, index) => (
                    <tr key={index}>
                      <td className="align-top">
                        <Form.Control type="text" value={producto.nombre} maxLength={80}
                          onChange={(e) => actualizarProducto(index, 'nombre', e.target.value.toUpperCase())} />
                      </td>
                      <td className="align-top">
                        <Form.Control as="textarea" rows={2} value={producto.descripcion} maxLength={1000}
                          style={{ resize: 'vertical', minHeight: 38 }}
                          onChange={(e) => actualizarProducto(index, 'descripcion', e.target.value.toUpperCase())} />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={inputsTemporales[index]?.cantidad ?? formatearMiles(producto.cantidad)}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (!/^[\d.,]*$/.test(input)) return;
                            const sinPuntos = input.replace(/\./g, '').replace(',', '.');
                            if (/^(\d{0,12})(\.\d{0,2})?$/.test(sinPuntos)) {
                              setInputsTemporales((prev) => ({ ...prev, [index]: { ...prev[index], cantidad: input } }));
                            }
                          }}
                          onBlur={() => {
                            const valor = inputsTemporales[index]?.cantidad || '';
                            const numero = parseFloat(valor.replace(/\./g, '').replace(',', '.'));
                            if (!isNaN(numero)) {
                              actualizarProducto(index, 'cantidad', numero);
                              setInputsTemporales((prev) => ({ ...prev, [index]: { ...prev[index], cantidad: formatearMiles(numero) } }));
                            }
                          }}
                          onFocus={() => {
                            setInputsTemporales((prev) => ({
                              ...prev,
                              [index]: { ...prev[index], cantidad: producto.cantidad?.toString().replace('.', ',') || '' },
                            }));
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control type="text" value={producto.unidad} maxLength={4}
                          onChange={(e) => actualizarProducto(index, 'unidad', e.target.value)} />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={inputsTemporales[index]?.precio ?? formatearMiles(producto.precio)}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (!/^[\d.,]*$/.test(input)) return;
                            const sinPuntos = input.replace(/\./g, '').replace(',', '.');
                            if (/^(\d{0,12})(\.\d{0,2})?$/.test(sinPuntos)) {
                              setInputsTemporales((prev) => ({ ...prev, [index]: { ...prev[index], precio: input } }));
                            }
                          }}
                          onBlur={() => {
                            const valor = inputsTemporales[index]?.precio || '';
                            const numero = parseFloat(valor.replace(/\./g, '').replace(',', '.'));
                            if (!isNaN(numero)) {
                              actualizarProducto(index, 'precio', numero);
                              setInputsTemporales((prev) => ({ ...prev, [index]: { ...prev[index], precio: formatearMiles(numero) } }));
                            }
                          }}
                          onFocus={() => {
                            setInputsTemporales((prev) => ({
                              ...prev,
                              [index]: { ...prev[index], precio: producto.precio?.toString().replace('.', ',') || '' },
                            }));
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={producto.descuento === 0 ? '' : producto.descuento}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (/^\d{0,3}([.,]\d{0,2})?$/.test(input) || input === '') {
                              actualizarProducto(index, 'descuento', input);
                            }
                          }}
                          onBlur={(e) => {
                            let valor = e.target.value.replace(',', '.');
                            let numero = parseFloat(valor);
                            if (isNaN(numero) || numero < 0) numero = 0;
                            if (numero > 100) numero = 100;
                            actualizarProducto(index, 'descuento', +numero.toFixed(2));
                          }}
                        />
                      </td>
                      <td className="text-end subtotal-cell">
                        {formatearMiles(calcularLinea(producto).monto)}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Eliminar línea"
                          onClick={() => eliminarProducto(index)}
                          className="row-action-btn"
                        >
                          <FaRegTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="form-section-action">
            <Button
              variant="primary"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  productos: [...prev.productos, { nombre: '', descripcion: '', cantidad: 1, unidad: 'UN', precio: 0, descuento: 0 }],
                }))
              }
            >
              <FaPlus className="me-2" />Agregar línea
            </Button>
          </div>
        </div>
      </div>

      <div className="form-section">
        <div className="section-header section-header-static">
          <div className="section-header-title">
            <FaCreditCard />
            <span>Forma de Pago</span>
          </div>
        </div>
        <div className="form-section-body">
          <Row>
            <Col md={6}>
              <Form.Select value={formData.formaPago} onChange={handleFormaPagoChange}>
                <option value="1">Contado</option>
                <option value="2">Crédito</option>
                <option value="3">Sin Costo</option>
              </Form.Select>
            </Col>
          </Row>
        </div>
      </div>

      <div className="form-section">
        <div className="section-header section-header-static">
          <div className="section-header-title">
            <FaCalculator />
            <span>Totales</span>
          </div>
        </div>
        <div className="form-section-body">
          <div className="totals-grid">
            {esExenta ? (
              <div className="total-row">
                <span className="total-label">Monto Exento</span>
                <span className="total-value">{formatearMiles(subtotal)}</span>
              </div>
            ) : (
              <>
                <div className="total-row">
                  <span className="total-label">Sub Total</span>
                  <span className="total-value">{formatearMiles(subtotal)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Monto Neto</span>
                  <span className="total-value">{formatearMiles(montoNeto)}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">IVA ({tasaIVA}%)</span>
                  <span className="total-value">{formatearMiles(montoIVA)}</span>
                </div>
              </>
            )}
            <div className="total-row total-row-grand">
              <span className="total-label">Total</span>
              <span className="total-value">${formatearMiles(total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="factura-actions">
        <Button variant="outline-secondary" onClick={handleLimpiarFormulario}>
          <FaEraser className="me-2" />Limpiar
        </Button>
        <Button
          variant="primary"
          onClick={handleVisuavilizar}
          disabled={loading || cargandoSucursales || faltaSucursal}
          title={faltaSucursal ? 'Selecciona la sucursal desde la que se emite el documento' : undefined}
        >
          <FaEye className="me-2" />Validar y visualizar
        </Button>
      </div>

      {/* Modales */}
      <Modal show={mostrarModalErrores} onHide={() => setMostrarModalErrores(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-warning me-2" />
            Errores en los datos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="error-list">
            {erroresReceptor.map((error, index) => <li key={index}>{error}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMostrarModalErrores(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={mostrarModalErroresProducto} onHide={() => setMostrarModalErroresProducto(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-warning me-2" />
            Errores en el detalle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="error-list">
            {erroresProducto.map((error, index) => <li key={index}>{error}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMostrarModalErroresProducto(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="m-0 modal-message">Cargando previsualización…</p>
          <p className="text-muted small m-0 mt-2">{nombreDoc}</p>
        </Modal.Body>
      </Modal>

      <Modal show={mostrarModalReferenciaInvalida} onHide={() => setMostrarModalReferenciaInvalida(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-warning me-2" />
            Errores en referencias
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="error-list">
            {erroresReferencia.map((error, i) => <li key={i}>{error}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setMostrarModalReferenciaInvalida(false)}>Entendido</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={mostrarModalErrorApi} onHide={() => setMostrarModalErrorApi(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-danger me-2" />
            Error al enviar la nota
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p className="m-0">{errorApi}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMostrarModalErrorApi(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={mostrarModalPdf} onHide={() => setMostrarModalPdf(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileInvoiceDollar className="me-2 text-primary" />
            Previsualización · {nombreDoc}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Vista previa de la nota"
              width="100%"
              height="800px"
              style={{ border: 'none', display: 'block' }}
            />
          ) : (
            <div className="loading-inline"><Spinner animation="border" variant="primary" /><p>Cargando PDF…</p></div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMostrarModalPdf(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleEnviarNota}>
            Confirmar y enviar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={mostrarModalEnvio}
        onHide={cerrarModalEnvio}
        centered
        backdrop={estadoEnvio.estado === 'PROCESANDO' ? 'static' : true}
        keyboard={estadoEnvio.estado !== 'PROCESANDO'}
      >
        <Modal.Header closeButton={estadoEnvio.estado !== 'PROCESANDO'}>
          <Modal.Title>
            <FaFileInvoiceDollar className="me-2 text-primary" />
            Envío de {esNotaCredito ? 'nota de crédito' : 'nota de débito'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {estadoEnvio.estado === 'PROCESANDO' && (
            <>
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="m-0">Estamos procesando el documento…</p>
              <p className="text-muted small m-0">{nombreDoc}</p>
              <p className="text-muted small m-0">Esto puede tardar unos segundos. No cierres esta ventana.</p>
            </>
          )}

          {estadoEnvio.estado === 'GENERADO' && (
            <>
              <FaFileInvoiceDollar className="text-success mb-3" size={40} />
              <p className="m-0 fw-bold">¡Documento generado correctamente!</p>
              <p className="text-muted small mb-0 mt-2">
                Folio N° {estadoEnvio.data?.folio} · Emisión {estadoEnvio.data?.fechaEmision}
              </p>
            </>
          )}

          {estadoEnvio.estado === 'TIMEOUT' && (
            <>
              <FaExclamationTriangle className="text-warning mb-3" size={40} />
              <p className="m-0">El documento aún se está procesando.</p>
              <p className="text-muted small mb-0 mt-2">
                Puedes consultar el folio más tarde en la búsqueda de documentos.
              </p>
            </>
          )}

          {estadoEnvio.estado === 'RECHAZADO' && (
            <>
              <FaExclamationTriangle className="text-danger mb-3" size={40} />
              <p className="m-0">El documento no pasó la validación del servidor.</p>
              <p className="text-muted small mb-0 mt-2">No se emitió ni se consumió folio.</p>
              <ul className="error-list text-start mt-3">
                {estadoEnvio.errores.map((error, i) => <li key={i}>{error}</li>)}
              </ul>
            </>
          )}

          {estadoEnvio.estado === 'ERROR' && (
            <>
              <FaExclamationTriangle className="text-danger mb-3" size={40} />
              <p className="m-0">Ocurrió un error al generar el documento.</p>
              <p className="text-muted small mb-0 mt-2">
                {estadoEnvio.mensaje || 'Inténtalo nuevamente más tarde.'}
              </p>
            </>
          )}
        </Modal.Body>
        {estadoEnvio.estado !== 'PROCESANDO' && (
          <Modal.Footer>
            {estadoEnvio.estado === 'GENERADO' && (
              <Button variant="primary" onClick={descargarPdfGenerado} disabled={descargandoPdf}>
                {descargandoPdf ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Generando PDF…</>
                ) : (
                  'Descargar PDF'
                )}
              </Button>
            )}
            <Button variant="secondary" onClick={cerrarModalEnvio} disabled={descargandoPdf}>Cerrar</Button>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
}

export default NotaCreditoDebitoFormulario;
